
import { supabase } from '@/integrations/supabase/client';
import { FutureRelease, InstallmentGroup } from '@/types';
import { getCurrentActiveProfileId } from './supabase-profiles-operations';
import { encryptField, decryptField } from './encryption-service';
import { logger } from '@/utils/logger';

export const saveFutureReleaseToSupabase = async (release: FutureRelease): Promise<void> => {
  logger.debug('[Supabase FutureReleases] Saving release with profile:', getCurrentActiveProfileId());
  
  try {
    // Encrypt sensitive fields
    const nameEncrypted = await encryptField(release.name);
    const descriptionEncrypted = await encryptField(release.description || '');
    const amountEncrypted = await encryptField(String(release.amount));
    
    const releaseData = {
      id: release.id,
      name: '[ENCRYPTED]',
      name_encrypted: nameEncrypted.encrypted,
      name_nonce: nameEncrypted.nonce,
      description: '[ENCRYPTED]',
      description_encrypted: descriptionEncrypted.encrypted,
      description_nonce: descriptionEncrypted.nonce,
      amount: 0,
      amount_encrypted: amountEncrypted.encrypted,
      amount_nonce: amountEncrypted.nonce,
      type: release.type,
      category_id: release.categoryId,
      account_id: release.accountId || null,
      card_id: release.cardId || null,
      payment_method: release.paymentMethod,
      due_date: release.dueDate || null,
      is_recurrent: release.isRecurrent,
      recurrence: release.recurrence,
      recurrence_end_date: release.recurrenceEndDate || null,
      status: release.status,
      installment_group_id: release.installmentGroupId || null,
      installment_number: release.installmentNumber || null,
      paid_date: release.paidDate || null,
      profile_id: getCurrentActiveProfileId(),
      user_id: (await supabase.auth.getUser()).data.user?.id
    };

    const { error } = await supabase
      .from('future_releases')
      .upsert(releaseData);

    if (error) {
      console.error('[Supabase FutureReleases] Error saving release:', error);
      throw new Error(`Erro ao salvar lançamento: ${error.message}`);
    }

    logger.debug('[Supabase FutureReleases] Release saved successfully:', release.id);
  } catch (error) {
    console.error('[Supabase FutureReleases] Encryption error:', error);
    throw new Error('Erro ao criptografar dados do lançamento');
  }
};

export const loadFutureReleasesFromSupabase = async (): Promise<FutureRelease[]> => {
  const profileId = getCurrentActiveProfileId();
  logger.debug('[Supabase FutureReleases] Loading releases for profile:', profileId);

  let query = supabase
    .from('future_releases')
    .select('*')
    .order('due_date', { ascending: true });

  // Filter by profile if one is active
  if (profileId) {
    query = query.eq('profile_id', profileId);
  } else {
    // If no profile is active, return empty result to enforce isolation
    query = query.is('profile_id', null);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[Supabase FutureReleases] Error loading releases:', error);
    throw new Error(`Erro ao carregar lançamentos: ${error.message}`);
  }

  const releases: FutureRelease[] = await Promise.all((data || []).map(async (row) => {
    // Decrypt sensitive fields
    const name = await decryptField({
      encrypted: row.name_encrypted,
      nonce: row.name_nonce
    });
    
    const description = await decryptField({
      encrypted: row.description_encrypted,
      nonce: row.description_nonce
    });
    
    const amount = await decryptField({
      encrypted: row.amount_encrypted,
      nonce: row.amount_nonce
    });
    
    // Helper function to clean encrypted placeholders
    const cleanValue = (decrypted: string, fallback: string | null | undefined): string => {
      if (decrypted) return decrypted;
      if (!fallback || fallback === '[ENCRYPTED]') return '';
      return fallback;
    };
    
    return {
      id: row.id,
      name: cleanValue(name, row.name),
      description: cleanValue(description, row.description),
      amount: amount ? Number(amount) : Number(row.amount || 0),
      type: row.type as 'receita' | 'despesa',
      categoryId: row.category_id,
      accountId: row.account_id,
      cardId: row.card_id,
      paymentMethod: row.payment_method as any,
      dueDate: row.due_date,
      isRecurrent: row.is_recurrent || false,
      recurrence: row.recurrence as any || 'unica',
      recurrenceEndDate: row.recurrence_end_date,
      status: row.status as any,
      installmentGroupId: row.installment_group_id,
      installmentNumber: row.installment_number || 1,
      paidDate: row.paid_date,
      createdAt: row.created_at
    };
  }));

  logger.info(`[Supabase FutureReleases] Loaded ${releases.length} releases`);
  return releases;
};

export const deleteFutureReleaseFromSupabase = async (releaseId: string): Promise<void> => {
  logger.debug('[Supabase FutureReleases] Deleting release:', releaseId);

  const { error } = await supabase
    .from('future_releases')
    .delete()
    .eq('id', releaseId);

  if (error) {
    console.error('[Supabase FutureReleases] Error deleting release:', error);
    throw new Error(`Erro ao excluir lançamento: ${error.message}`);
  }
};

export const saveInstallmentGroupToSupabase = async (group: InstallmentGroup): Promise<void> => {
  logger.debug('[Supabase InstallmentGroups] Saving group with profile:', getCurrentActiveProfileId());
  
  try {
    // Encrypt sensitive fields
    const nameEncrypted = await encryptField(group.name);
    const amountEncrypted = await encryptField(String(group.amount));
    
    const groupData = {
      id: group.id,
      name: '[ENCRYPTED]',
      name_encrypted: nameEncrypted.encrypted,
      name_nonce: nameEncrypted.nonce,
      category_id: group.categoryId,
      amount: 0,
      amount_encrypted: amountEncrypted.encrypted,
      amount_nonce: amountEncrypted.nonce,
      start_date: group.startDate,
      end_date: group.endDate,
      total_installments: group.totalInstallments,
      paid_installments: group.paidInstallments,
      status: group.status,
      type: group.type,
      account_id: group.accountId,
      payment_method: group.paymentMethod,
      card_id: group.cardId,
      profile_id: getCurrentActiveProfileId(),
      user_id: (await supabase.auth.getUser()).data.user?.id
    };

    const { error } = await supabase
      .from('installment_groups')
      .upsert(groupData);

    if (error) {
      console.error('[Supabase InstallmentGroups] Error saving group:', error);
      throw new Error(`Erro ao salvar grupo de parcelas: ${error.message}`);
    }

    logger.debug('[Supabase InstallmentGroups] Group saved successfully:', group.id);
  } catch (error) {
    console.error('[Supabase InstallmentGroups] Encryption error:', error);
    throw new Error('Erro ao criptografar dados do grupo de parcelas');
  }
};

export const loadInstallmentGroupsFromSupabase = async (): Promise<InstallmentGroup[]> => {
  const profileId = getCurrentActiveProfileId();
  logger.debug('[Supabase InstallmentGroups] Loading groups for profile:', profileId);

  let query = supabase
    .from('installment_groups')
    .select('*')
    .order('start_date', { ascending: true });

  // Filter by profile if one is active
  if (profileId) {
    query = query.eq('profile_id', profileId);
  } else {
    // If no profile is active, return empty result to enforce isolation
    query = query.is('profile_id', null);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[Supabase InstallmentGroups] Error loading groups:', error);
    throw new Error(`Erro ao carregar grupos de parcelas: ${error.message}`);
  }

  const groups: InstallmentGroup[] = await Promise.all((data || []).map(async (row) => {
    // Decrypt sensitive fields
    const name = await decryptField({
      encrypted: row.name_encrypted,
      nonce: row.name_nonce
    });
    
    const amount = await decryptField({
      encrypted: row.amount_encrypted,
      nonce: row.amount_nonce
    });
    
    return {
      id: row.id,
      name: name || row.name,
      categoryId: row.category_id,
      amount: amount ? Number(amount) : Number(row.amount || 0),
      startDate: row.start_date,
      endDate: row.end_date,
      totalInstallments: row.total_installments,
      paidInstallments: row.paid_installments || 0,
      status: row.status as any,
      type: row.type as 'receita' | 'despesa',
      accountId: row.account_id,
      paymentMethod: row.payment_method as any,
      cardId: row.card_id,
      transactionIds: [],
      createdAt: row.created_at
    };
  }));

  logger.info(`[Supabase InstallmentGroups] Loaded ${groups.length} groups`);
  return groups;
};

export const deleteInstallmentGroupFromSupabase = async (groupId: string): Promise<void> => {
  logger.debug('[Supabase InstallmentGroups] Deleting group:', groupId);

  const { error } = await supabase
    .from('installment_groups')
    .delete()
    .eq('id', groupId);

  if (error) {
    console.error('[Supabase InstallmentGroups] Error deleting group:', error);
    throw new Error(`Erro ao excluir grupo de parcelas: ${error.message}`);
  }
};

export const isUserAuthenticated = async (): Promise<boolean> => {
  const { data: { user } } = await supabase.auth.getUser();
  return !!user;
};
